import React, { useState } from 'react';
import './styles/xp-styles.css'; // Import XP.css scoped to Desktop component
import Taskbar from './components/Taskbar';
import StartMenu from './components/StartMenu';
import DesktopIcon from './components/DesktopIcon';
import Window from './components/Window';
import ContextMenu from './components/ContextMenu';
import { APP_REGISTRY } from './config/appRegistry';
import useWindowManager from './hooks/useWindowManager';

const Desktop = ({ onClose }) => {
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  // Initialize positions from registry and localStorage
  const [iconPositions, setIconPositions] = useState(() => {
    try {
      const saved = localStorage.getItem('xp_icon_positions');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load icon positions from storage', e);
    }

    const positions = {};
    Object.values(APP_REGISTRY).forEach(app => {
      if (app.initialPosition) {
        positions[app.id] = app.initialPosition;
      }
    });
    return positions;
  });

  // Persist icon positions
  React.useEffect(() => {
    localStorage.setItem('xp_icon_positions', JSON.stringify(iconPositions));
  }, [iconPositions]);

  const {
    openWindows,
    openNativeApps,
    handleOpenApp,
    handleCloseWindow,
    handleCloseNativeApp,
    handleMinimizeWindow,
    handleMaximizeWindow,
    handleRestoreWindow,
    handleToggleMinimizeWindow
  } = useWindowManager();

  const handleStartClick = () => {
    console.log('Desktop handleStartClick called, current state:', isStartMenuOpen);
    setIsStartMenuOpen(!isStartMenuOpen);
  };

  const handleDesktopClick = () => {
    setIsStartMenuOpen(false);
    setContextMenu(null);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setIsStartMenuOpen(false);
    setContextMenu({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleIconPositionChange = (iconId, newPosition) => {
    setIconPositions(prev => ({
      ...prev,
      [iconId]: newPosition
    }));
  };

  const renderNativeAppContent = (appType, appId) => {
    const appConfig = APP_REGISTRY[appType];
    const Component = appConfig?.component;

    if (!Component) return null;

    switch (appType) {
      case 'winamp':
        return (
          <div className="absolute top-20 left-20 z-50">
            <Component />
          </div>
        );
      case 'paint':
        return (
          <div className="fixed inset-0 z-50 flex flex-col">
            {/* XP.css title bar for Paint */}
            <div className="title-bar">
              <div className="title-bar-text">
                <img
                  src={appConfig.icon}
                  alt={appConfig.title}
                  className="inline w-4 h-4 mr-2"
                  style={{ imageRendering: 'pixelated' }}
                />
                {appConfig.title}
              </div>
              <div className="title-bar-controls">
                <button
                  aria-label="Close"
                  onClick={() => handleCloseNativeApp(appId)}
                ></button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <Component />
            </div>
          </div>
        );
      default:
        return <div>Native app not found</div>;
    }
  };

  return (
    <div
      id="desktop-container"
      className="relative h-screen w-screen overflow-hidden cursor-default select-none"
      onClick={handleDesktopClick}
      onContextMenu={handleContextMenu}
      style={{
        backgroundImage: 'url(/windowsxp.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[9999] w-8 h-8 bg-red-600 hover:bg-red-700 text-white flex items-center justify-center rounded transition-colors duration-200 shadow-lg"
          aria-label="Close Windows XP"
          title="Return to Landing Screen"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}

      {/* Desktop Icons */}
      {Object.values(APP_REGISTRY).map(app => (
        <DesktopIcon
          key={app.id}
          icon={app.icon}
          label={app.label || app.title}
          position={iconPositions[app.id]}
          onPositionChange={(pos) => handleIconPositionChange(app.id, pos)}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenApp(app.id);
          }}
        />
      ))}

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
        />
      )}

      {/* Start Menu */}
      {isStartMenuOpen && (
        <StartMenu
          onClose={() => setIsStartMenuOpen(false)}
          onOpenApp={handleOpenApp}
        />
      )}

      {/* Native Apps */}
      {openNativeApps.map((app) => (
        <React.Fragment key={app.id}>
          {renderNativeAppContent(app.type, app.id)}
        </React.Fragment>
      ))}

      {/* Open Windows */}
      {openWindows
        .filter(window => !window.isMinimized)
        .map((window) => {
          const appConfig = APP_REGISTRY[window.type];
          const Component = appConfig?.component;

          if (!Component) return null;

          return (
            <Window
              key={window.id}
              title={appConfig.title}
              icon={appConfig.icon}
              onClose={() => handleCloseWindow(window.id)}
              onMinimize={() => handleMinimizeWindow(window.id)}
              onMaximize={() => handleMaximizeWindow(window.id)}
              isMaximized={window.isMaximized}
              x={window.x}
              y={window.y}
              width={window.width}
              height={window.height}
            >
              <Component />
            </Window>
          );
        })}

      {/* Taskbar */}
      <Taskbar
        onStartClick={handleStartClick}
        openWindows={openWindows}
        onRestoreWindow={handleRestoreWindow}
        onToggleMinimize={handleToggleMinimizeWindow}
        getWindowTitle={(type) => APP_REGISTRY[type]?.title || 'Unknown'}
        getWindowIcon={(type) => APP_REGISTRY[type]?.icon || '/default.gif'}
      />
    </div>
  );
};

export default Desktop;
